/** API client: bearer token, typed errors, offline detection. */
import type { ApiError } from '../../shared/api.ts';

export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}
export class OfflineError extends Error {
  constructor() {
    super('You appear to be offline.');
  }
}

let token: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setToken(t: string | null): void {
  token = t;
}
export function getToken(): string | null {
  return token;
}
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

export interface ApiOptions {
  auth?: boolean;
  timeoutMs?: number;
  /** Let the request finish although the page is going away (bodies under 64 KB only). */
  keepalive?: boolean;
}

export async function apiFetch(path: string, init: RequestInit = {}, opts: ApiOptions = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  if (opts.auth !== false && token) headers.set('authorization', `Bearer ${token}`);
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? 30_000);
  let res: Response;
  try {
    res = await fetch(path, { ...init, headers, credentials: 'omit', cache: 'no-store', signal: ctrl.signal, redirect: 'manual', keepalive: opts.keepalive === true });
  } catch {
    throw new OfflineError();
  } finally {
    clearTimeout(timer);
  }
  if (res.type === 'opaqueredirect' || res.status === 0) throw new OfflineError();
  if (res.status === 401 && opts.auth !== false) {
    onUnauthorized?.();
    throw new ApiFailure(401, 'unauthorized', 'Please sign in again.');
  }
  if (!res.ok) {
    let err: ApiError = { error: res.statusText || 'Request failed', code: 'http_' + res.status };
    try {
      err = (await res.json()) as ApiError;
    } catch {
      /* ignore */
    }
    throw new ApiFailure(res.status, err.code, err.error);
  }
  return res;
}

export async function apiJson<T>(path: string, init: RequestInit = {}, opts?: ApiOptions): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && typeof init.body === 'string' && !headers.has('content-type')) headers.set('content-type', 'application/json');
  const res = await apiFetch(path, { ...init, headers }, opts);
  return (await res.json()) as T;
}

export function describeError(e: unknown): string {
  if (e instanceof OfflineError) return e.message;
  if (e instanceof ApiFailure) return e.message;
  if (e instanceof Error) return e.message;
  return String(e);
}
